import {
  Alert,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import React, { useContext, useState } from 'react';
import JBWDateLabel from './JBWDateLabel';
import { AuthContext } from '../AuthContext';
import { getInvoices } from '../services/InvoiceService';
import { createPotentialClaim } from '../services/ClaimsServices';
import dayjs from "dayjs";

const CreateClaimDialog = ({ open, onClose, dealer, tenant }) => {
  const { user } = useContext(AuthContext);
  const [jobNumber, setJobNumber] = useState('');
  const [dealerIdInput, setDealerIdInput] = useState('');
  const [dealerIdError, setDealerIdError] = useState(false);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [invoice, setInvoice] = useState(null);
  const [selectedAgreement, setSelectedAgreement] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [claimValidationErrors, setClaimValidationErrors] = useState([]);
  const isDealerMissing = !dealer;

  const handleClose = () => {
    setClaimValidationErrors([]);
    setJobNumber('')
    setDealerIdInput('')
    setDealerIdError(false)
    setInvoice(null)
    setSelectedAgreement(null)
    setSearchError('')
    setSaveError('')
    onClose()
  }

  const handleSearch = () => {
    if (!jobNumber.trim()) return
    if (isDealerMissing && !dealerIdInput.trim()) {
      setDealerIdError(true)
      return
    }
    setDealerIdError(false);
    setSearching(true);
    setSearchError('');
    setInvoice(null);
    setSelectedAgreement(null);
    setClaimValidationErrors([]);

    const dealerId = isDealerMissing ? dealerIdInput.trim() : (dealer?.dealerId || 0)
    const tenantId = tenant || dealer?.tenantId || 0

    getInvoices(dealerId, tenantId, jobNumber.trim())
      .then((res) => {
        const data = res.data
        if (data && data.length > 0) {
          setInvoice(data[0])
          const agreements = data[0].locationAgreements
          if (agreements && agreements.length === 1) {
            setSelectedAgreement(agreements[0]);
            validateClaimEligibility(data[0], agreements[0]);
          }
        } else {
          setSearchError('No invoice found for this job number.')
        }
      })
      .catch(() => setSearchError('Failed to locate this invoice. Please try again'))
      .finally(() => setSearching(false))
  }

  const validateClaimEligibility = (invoice, agreement) => {
    if (!invoice || !agreement) {
      setClaimValidationErrors([]);
      return;
    }
    console.log(" Dealer details :: ", dealer);

    const errors = [];

    const serviceDate = dayjs(invoice.serviceStartDate);
    const today = dayjs();

    // 1. Coverage Start Date
    if (
      agreement.coverageStartDate &&
      serviceDate.isBefore(dayjs(agreement.coverageStartDate), "day")
    ) {
      errors.push(
        "Fail Date cannot be earlier than the Coverage Start Date."
      );
    }

    // 2. Claim Waiting Period
    if (
      agreement.startDate &&
      dealer?.claimWaitingPeriod != null
    ) {
      const waitingDays = serviceDate.diff(
        dayjs(agreement.startDate),
        "day"
      );

      if (waitingDays < dealer.claimWaitingPeriod) {
        errors.push(
          "The Service Date is within the Claim Waiting Period. A claim cannot be created until the waiting period has elapsed."
        );
      }
    }

    // 3. Claim Exception Days
    if (dealer?.claimExceptionDays != null) {
      const exceptionDays = today.diff(serviceDate, "day");

      if (exceptionDays > dealer.claimExceptionDays) {
        errors.push(
          "The Service Date exceeds the allowable Claim Exception Days. Please contact the claims and/or support team."
        );
      }
    }

    // 4. Agreement Expiration Date
    if (
      agreement.expirationDate &&
      serviceDate.isAfter(dayjs(agreement.expirationDate), "day")
    ) {
      errors.push(
        "The Service Date is after the Agreement Expiration Date. Please contact the claims and/or support team."
      );
    }

    setClaimValidationErrors(errors);
  };

  const handleSave = () => {
    if (claimValidationErrors.length > 0) {
      return;
    }
    if (!invoice || !selectedAgreement) return
    const now = new Date().toISOString()
    const payload = {
      tenantId: invoice.tenantId,
      dealerId: invoice.dealerId,
      invoiceId: invoice.invoiceId,
      jobNumber: invoice.jobNumber,
      jobId: invoice.jobId,
      businessUnitId: invoice.businessUnitId,
      locationId: invoice.locationId,
      serviceDate: invoice.stCreatedOn,
      claimSummary: invoice.invoiceSummary,
      productCode: selectedAgreement.productCode,
      jbwAgreementId: selectedAgreement.jbwAgreementId,
      customerName: `${invoice.firstName || ''} ${invoice.lastName || ''}`.trim(),
      addressLine1: invoice.addressLine1,
      addressLine2: invoice.addressLine2,
      city: invoice.city,
      state: invoice.state,
      zipcode: invoice.zipcode,
      claimStatus: 'Pending',
      createdOn: now,
      createdBy: user?.name || user?.username || user?.email,
      modifiedOn: now,
      modifiedBy: user?.name || user?.username || user?.email,
    }

    setSaving(true)
    setSaveError('')
    createPotentialClaim(payload, invoice.dealerId)
      .then(() => {
        setSaving(false)
        handleClose()
      })
      .catch((err) => {
        setSaving(false)
        setSaveError(err?.response?.data?.message || 'Failed to create claim. Please try again.')
      })
  }

  const hasAgreements = invoice?.locationAgreements?.length > 0
  const multipleAgreements = invoice?.locationAgreements?.length > 1
  const canSave = invoice && selectedAgreement

  return (
    <Dialog open={open} fullWidth maxWidth="lg" disableEscapeKeyDown>
      <DialogTitle>
        New Claim
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8, color: (t) => t.palette.grey[500] }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {/* Job Number Search */}
          <Stack direction="row" spacing={1} alignItems="flex-start">
            {isDealerMissing && (
              <TextField
                label="Dealer ID"
                size="small"
                required
                value={dealerIdInput}
                error={dealerIdError}
                helperText={dealerIdError ? 'Dealer ID is required' : ''}
                onChange={(e) => {
                  setDealerIdInput(e.target.value)
                  if (dealerIdError) setDealerIdError(false)
                }}
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                sx={{ width: 180 }}
              />
            )}
            <TextField
              label="Job Number"
              size="small"
              value={jobNumber}
              onChange={(e) => setJobNumber(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              sx={{ width: 260 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {searching && <CircularProgress size={18} />}
                  </InputAdornment>
                ),
              }}
            />
            <Button
              variant="contained"
              startIcon={<SearchIcon />}
              onClick={handleSearch}
              disabled={searching || !jobNumber.trim() || (isDealerMissing && !dealerIdInput.trim())}
              sx={{ mt: '4px' }}
            >
              Search
            </Button>
          </Stack>

          {searchError && <Alert severity="warning">{searchError}</Alert>}
          {saveError && <Alert severity="error" onClose={() => setSaveError('')}>{saveError}</Alert>}
          {claimValidationErrors.length > 0 && (
            <Alert severity="warning">
              {claimValidationErrors.map((msg, index) => (
                <Typography key={index} variant="body2">
                  • {msg}
                </Typography>
              ))}
            </Alert>
          )}

          {/* Invoice Details */}
          {invoice && (
            <Paper elevation={3} sx={{ p: 1 }}>
              <Typography variant="subtitle1" sx={{ ml: 1, mb: 1, fontWeight: 'bold' }}>
                Invoice Details
              </Typography>
              <TableContainer>
                <Table size="small" aria-label="Invoice Details">
                  <TableHead>
                    <TableRow>
                      <TableCell>Invoice #</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Summary</TableCell>
                      <TableCell>Job Number</TableCell>
                      <TableCell>Customer Name</TableCell>
                      <TableCell>Phone &amp; Email</TableCell>
                      <TableCell>Address</TableCell>
                      <TableCell>ST Created On</TableCell>
                      <TableCell>ST Modified On</TableCell>
                      <TableCell>Service Start Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                      <TableCell>{invoice.invoiceId}</TableCell>
                      <TableCell>{invoice.invoiceStatus}</TableCell>
                      <TableCell>{invoice.invoiceSummary}</TableCell>
                      <TableCell>{invoice.jobNumber}</TableCell>
                      <TableCell>{invoice.firstName} {invoice.lastName}</TableCell>
                      <TableCell>
                        {invoice.phoneNumber && invoice.phoneNumber !== 'null' ? invoice.phoneNumber : ''}
                        {invoice.emailAddress && invoice.emailAddress !== 'null' ? `, ${invoice.emailAddress}` : ''}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ wordWrap: 'break-word', whiteSpace: 'normal' }}>
                          {`${invoice.addressLine1 || ''}${invoice.addressLine2 && invoice.addressLine2 !== 'null' ? ', ' + invoice.addressLine2 : ''}${invoice.city && invoice.city !== 'null' ? ', ' + invoice.city : ''}${invoice.state && invoice.state !== 'null' ? ', ' + invoice.state : ''}${invoice.zipcode && invoice.zipcode !== 'null' ? ', ' + invoice.zipcode : ''}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {invoice.stCreatedOn && <JBWDateLabel inputDate={invoice.stCreatedOn} dateFormat="MM/DD/YYYY" />}
                      </TableCell>
                      <TableCell>
                        {invoice.stModifiedOn && <JBWDateLabel inputDate={invoice.stModifiedOn} dateFormat="MM/DD/YYYY" />}
                      </TableCell>
                      <TableCell>
                        {invoice.serviceStartDate && <JBWDateLabel inputDate={invoice.serviceStartDate} dateFormat="MM/DD/YYYY" />}
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Location Agreements */}
          {hasAgreements && (
            <Paper elevation={3} sx={{ p: 1 }}>
              <Typography variant="subtitle1" sx={{ ml: 1, mb: 1, fontWeight: 'bold' }}>
                Location Agreements
                {multipleAgreements && (
                  <Typography variant="caption" color="warning.main" sx={{ ml: 1 }}>
                    — select one to proceed
                  </Typography>
                )}
              </Typography>
              <TableContainer>
                <Table size="small" aria-label="Location Agreements">
                  <TableHead>
                    <TableRow>
                      <TableCell>Product Code</TableCell>
                      <TableCell>Agreement Id</TableCell>
                      <TableCell>Agreement Status</TableCell>
                      <TableCell>Agreement Link</TableCell>
                      <TableCell>Purchase Date</TableCell>
                      <TableCell>Start Date</TableCell>
                      <TableCell>Coverage Start Date</TableCell>
                      <TableCell>Expiration Date</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {invoice.locationAgreements.map((agr, idx) => (
                      <TableRow
                        key={idx}
                        onClick={() => {
                          setSelectedAgreement(agr);
                          validateClaimEligibility(invoice, agr);
                        }}
                        sx={{
                          '&:last-child td, &:last-child th': { border: 0 },
                          cursor: multipleAgreements ? 'pointer' : 'default',
                          backgroundColor: selectedAgreement === agr ? '#bbdefb' : 'inherit',
                          '&:hover': multipleAgreements ? { backgroundColor: '#e3f2fd' } : {},
                        }}
                      >
                        <TableCell>{agr.productCode}</TableCell>
                        <TableCell>{agr.jbwAgreementId}</TableCell>
                        <TableCell>{agr.agreementStatus}</TableCell>
                        <TableCell>{agr.agreementLink}</TableCell>
                        <TableCell>{agr.purchaseDate && <JBWDateLabel inputDate={agr.purchaseDate} dateFormat="MM/DD/YYYY" />}</TableCell>
                        <TableCell>{agr.startDate && <JBWDateLabel inputDate={agr.startDate} dateFormat="MM/DD/YYYY" />}</TableCell>
                        <TableCell>{agr.coverageStartDate && <JBWDateLabel inputDate={agr.coverageStartDate} dateFormat="MM/DD/YYYY" />}</TableCell>
                        <TableCell>{agr.expirationDate && <JBWDateLabel inputDate={agr.expirationDate} dateFormat="MM/DD/YYYY" />}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {invoice && !hasAgreements && (
            <Alert severity="info">No location agreements found for this invoice.</Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={saving}>Close</Button>
        <Button
          variant="contained"
          color="success"
          onClick={handleSave}
          disabled={!canSave || saving || claimValidationErrors.length > 0}
          startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {saving ? 'Saving...' : 'Create Claim'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CreateClaimDialog
